import type { Country } from './schemas';

/**
 * Manual per-country corrections layered on top of the upstream Natural
 * Earth dataset at load time. Two kinds of override are supported today:
 *
 *   - `displayName`  — replace the user-facing `name`. Use sparingly:
 *                      only for the cases where the upstream long-form
 *                      official name reads worse than the common name
 *                      (e.g. "People's Republic of China" → "China").
 *
 *   - `forceDot`     — set `needs_dot = true` even if the polygon
 *                      passes the auto-place size threshold. The
 *                      Gambia is geographically thin enough that the
 *                      auto-place misses it but it's near-impossible
 *                      to click without a dot.
 *
 * Applied once in `loadCountries` (the in-memory cache means the
 * mutation runs on first load and every subsequent call sees the
 * corrected data — no per-consumer re-application needed).
 *
 * **Becomes admin-managed later.** This file's shape is what the
 * future admin CMS endpoint will return; swapping this constant for
 * a fetch is the only change needed when that lands.
 */
export interface CountryOverride {
  displayName?: string;
  forceDot?: boolean;
}

export const COUNTRY_OVERRIDES: Record<string, CountryOverride> = {
  CN: { displayName: 'China' },
  GM: { forceDot: true },
  BN: { forceDot: true },
};

/** Mutate a country in place to reflect any matching override. Idempotent. */
export function applyCountryOverride(country: Country): void {
  const override = COUNTRY_OVERRIDES[country.iso_a2];
  if (!override) return;
  if (override.displayName !== undefined) {
    country.name = override.displayName;
  }
  if (override.forceDot) {
    country.needs_dot = true;
  }
}
